/**
 * NDG Films Grid — view.js
 *
 * WP Interactivity : filtrage par catégorie, tri, bascule grille/liste.
 * Hover vidéo (mp4 + YouTube) sur les cartes en mode grille.
 */

import { store, getContext, getElement } from '@wordpress/interactivity';
import { hasAnalyticsConsent } from '../../js/front/consent-check.js';

const { state } = store( 'ndg/films-grid', {

	state: {
		category: 0,
		sort:     'recent',
		view:     'grid',
	},

	// ── Dérivés ──────────────────────────────────────────────────────────────
	callbacks: {

		isListView() {
			return state.view === 'list';
		},

		isCatActive() {
			return getContext().cat === state.category;
		},

		isViewActive() {
			return getContext().view === state.view;
		},

		isItemHidden() {
			const { catIds = [] } = getContext();
			return state.category !== 0 && ! catIds.includes( state.category );
		},

		// Reveal instantané quand le filtre catégorie ré-affiche un item.
		// Au premier passage (hydratation), on laisse l'IntersectionObserver
		// gérer le fade-in au scroll comme d'habitude — il ne s'agit ici que
		// de contourner son délai/fragilité lors des changements de filtre
		// ultérieurs (display:none → visible), qui ne sont pas fiables à
		// 100% selon les navigateurs.
		revealOnFilter() {
			const { ref } = getElement();
			const ctx      = getContext();
			const { catIds = [] } = ctx;
			const hidden = state.category !== 0 && ! catIds.includes( state.category );

			if ( ! ctx.hasMounted ) {
				ctx.hasMounted = true;
				return;
			}

			if ( ! hidden ) ref.classList.add( 'is-visible' );
		},

		// Initialise le hover vidéo sur tous les .ndg-fgrid__item du conteneur
		initHover() {
			const { ref } = getElement();
			if ( ! ref ) return;

			const canHover = window.matchMedia( '(hover: hover) and (pointer: fine)' ).matches;
			if ( ! canHover ) return;

			ref.querySelectorAll( '.ndg-fgrid__item' ).forEach( ( item ) => {
				const videoType = item.dataset.videoType;
				if ( ! videoType || videoType === 'none' ) return;

				const videoEl      = item.querySelector( '.ndg-film-card__video:not(.ndg-film-card__video--yt)' );
				const ytPlaceholder = item.querySelector( '.ndg-film-card__video--yt' );
				const soundBtn     = item.querySelector( '.ndg-film-card__sound' );
				let   ytIframe     = null;
				let   isMuted      = true;

				const card = item.querySelector( '.ndg-film-card' );

				item.addEventListener( 'mouseenter', () => {
					item.classList.add( 'is-hovered' );
					if ( card ) card.classList.add( 'is-hovered' );

					if ( videoType === 'mp4' && videoEl ) {
						if ( ! videoEl.src && videoEl.dataset.src ) {
							videoEl.src = videoEl.dataset.src;
						}
						videoEl.muted = true;
						videoEl.play().catch( () => {} );

					} else if ( videoType === 'youtube' && ytPlaceholder ) {
						const ytId = item.dataset.ytId;
						if ( ! ytId || ! hasAnalyticsConsent() ) return;

						if ( ! ytIframe ) {
							ytIframe = document.createElement( 'iframe' );
							ytIframe.src = [
								`https://www.youtube-nocookie.com/embed/${ ytId }`,
								`?autoplay=1&mute=1&loop=1&playlist=${ ytId }`,
								`&controls=0&rel=0&iv_load_policy=3&modestbranding=1`,
								`&playsinline=1&enablejsapi=1`,
							].join( '' );
							ytIframe.allow       = 'autoplay; encrypted-media';
							ytIframe.frameBorder = '0';
							ytPlaceholder.appendChild( ytIframe );
						} else {
							ytIframe.contentWindow?.postMessage(
								JSON.stringify( { event: 'command', func: 'playVideo' } ), '*'
							);
						}
					}
				} );

				item.addEventListener( 'mouseleave', () => {
					item.classList.remove( 'is-hovered' );
					if ( card ) card.classList.remove( 'is-hovered' );
					isMuted = true;
					if ( soundBtn ) soundBtn.classList.remove( 'is-unmuted' );

					if ( videoType === 'mp4' && videoEl ) {
						videoEl.pause();
						videoEl.currentTime = 0;
						videoEl.muted       = true;

					} else if ( ytIframe ) {
						ytIframe.contentWindow?.postMessage(
							JSON.stringify( { event: 'command', func: 'pauseVideo' } ), '*'
						);
						ytIframe.contentWindow?.postMessage(
							JSON.stringify( { event: 'command', func: 'mute' } ), '*'
						);
					}
				} );

				if ( soundBtn ) {
					soundBtn.addEventListener( 'click', ( e ) => {
						e.preventDefault();
						e.stopPropagation();
						isMuted = ! isMuted;
						soundBtn.classList.toggle( 'is-unmuted', ! isMuted );

						if ( videoType === 'mp4' && videoEl ) {
							videoEl.muted = isMuted;
						} else if ( ytIframe ) {
							ytIframe.contentWindow?.postMessage(
								JSON.stringify( { event: 'command', func: isMuted ? 'mute' : 'unMute' } ), '*'
							);
						}
					} );
				}
			} );
		},
	},

	// ── Actions ───────────────────────────────────────────────────────────────
	actions: {

		setCategory() {
			state.category = getContext().cat;
		},

		setView() {
			state.view = getContext().view;
		},

		setSort( e ) {
			state.sort = e.target.value;

			// Réordonne les éléments dans le DOM
			const { ref } = getElement();
			const container = ref.closest( '.ndg-fgrid' )?.querySelector( '.ndg-fgrid__films' );
			if ( ! container ) return;

			const items = [ ...container.querySelectorAll( '.ndg-fgrid__item' ) ];
			items.sort( ( a, b ) => {
				if ( state.sort === 'az' ) {
					return ( a.dataset.title || '' ).localeCompare( b.dataset.title || '', 'fr', { sensitivity: 'base' } );
				}
				if ( state.sort === 'old' ) {
					return parseInt( a.dataset.year || 0 ) - parseInt( b.dataset.year || 0 );
				}
				// recent (défaut) : année décroissante
				return parseInt( b.dataset.year || 0 ) - parseInt( a.dataset.year || 0 );
			} );
			items.forEach( ( el ) => container.appendChild( el ) );
		},
	},
} );
